import React, { useRef, useEffect, useState } from "react";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { useKeyboard } from "../../context/KeyboardContext";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown } from "lucide-react";

const keyboardStyles = `
  .keyboard-container .hg-theme-default {
    background-color: transparent !important;
    padding: 0 !important;
  }
  .keyboard-container .hg-theme-default .hg-button {
    height: 50px !important;
    background: white !important;
    border-bottom: 2px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 1.1rem !important;
    font-weight: 500 !important;
    color: #4a5568 !important;
    transition: all 0.1s ease !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .keyboard-container .hg-theme-default .hg-button:active {
    background: #f7fafc !important;
    transform: translateY(1px) !important;
    border-bottom-width: 1px !important;
  }
  .keyboard-container .hg-theme-default .hg-button.hg-standardBtn {
    width: 20px !important;
  }
  .keyboard-container .hg-theme-default .hg-button.hg-functionBtn {
    background: #edf2f7 !important;
    font-size: 0.9rem !important;
    color: #718096 !important;
  }
  .keyboard-container .hg-theme-default .hg-button.hg-button-space {
    flex-grow: 5 !important;
  }
  .keyboard-container .hg-theme-default .hg-button.hg-button-enter {
    background: #885ABB !important;
    color: white !important;
    border-bottom-color: #7047a3 !important;
  }
`;

const VirtualKeyboard: React.FC = () => {
  const { isOpen, setIsOpen, activeInput, isEnabled, position } = useKeyboard();
  const keyboard = useRef<any>(null);
  const [layoutName, setLayoutName] = useState("default");
  const [inputValue, setInputValue] = useState("");

  // Sync keyboard input if activeInput changes while keyboard is already open
  useEffect(() => {
    if (activeInput && keyboard.current && isOpen) {
      const val = activeInput.value || "";
      if (keyboard.current.getInput() !== val) {
        keyboard.current.setInput(val);
        setInputValue(val);
      }
    }
  }, [activeInput, isOpen]);

  const onChange = (input: string) => {
    if (activeInput) {
      // Use the native value setter to bypass React's internal value tracking
      // This is necessary for React to "see" the change and update its state
      const prototype = activeInput instanceof HTMLTextAreaElement
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;

      const valueSetter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;

      if (valueSetter) {
        valueSetter.call(activeInput, input);
      } else {
        activeInput.value = input;
      }

      setInputValue(input);

      activeInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  const onKeyPress = (button: string) => {
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName(layoutName === "default" ? "shift" : "default");
    }
    if (button === "{enter}") {
      // Blur the input to indicate completion and allow clean re-focusing
      activeInput?.blur();
      setIsOpen(false);
    }
  };

  const handleClose = () => {
    activeInput?.blur();
    setIsOpen(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  if (!isEnabled) return null;

  // Calculate positioning and animation based on the 'position' setting
  const getPositionClasses = () => {
    switch (position) {
      case "Top Center":
        return "top-0 left-0 right-0 border-b";
      case "Top Left":
        return "top-4 left-4 w-[500px] border rounded-2xl";
      case "Top Right":
        return "top-4 right-4 w-[500px] border rounded-2xl";
      case "Bottom Center":
      default:
        return "bottom-0 left-0 right-0 border-t";
    }
  };

  const isTop = position.startsWith("Top");
  const initialY = isTop ? "-100%" : "100%";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>{keyboardStyles}</style>
          <motion.div
            initial={{ y: initialY, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: initialY, opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onMouseDown={handleMouseDown}
            className={`fixed z-[999999] bg-white border-gray-200 shadow-[0_10px_50px_rgba(0,0,0,0.2)] keyboard-container pb-6 ${getPositionClasses()}`}
          >
            <div className={`mx-auto p-4 ${position.includes("Center") ? "max-w-[900px]" : "w-full"}`}>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-4 px-2">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#885ABB] flex-shrink-0 animate-pulse" />
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold leading-none mb-1">
                      {activeInput?.placeholder || activeInput?.name || "Typing"}
                    </span>
                    <span className="text-sm font-bold text-gray-700 truncate min-h-[1.25rem]">
                      {activeInput?.type === "password" ? "•".repeat(inputValue.length) : (inputValue || "Start typing...")}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="flex items-center gap-2 px-6 py-2 bg-gray-100 hover:bg-gray-200 text-[#885ABB] font-bold rounded-xl transition-all active:scale-95"
                >
                  {isTop ? <ChevronDown size={18} className="rotate-180" /> : <ChevronDown size={18} />}
                  HIDE
                </button>
              </div>

              {/* Simple Keyboard */}
              <Keyboard
                keyboardRef={(r) => {
                  keyboard.current = r;
                  if (r && activeInput) {
                    const val = activeInput.value || "";
                    r.setInput(val);
                    setInputValue(val);
                  }
                }}
                layoutName={layoutName}
                onChange={onChange}
                onKeyPress={onKeyPress}
                theme={"hg-theme-default"}
                layout={{
                  default: [
                    "1 2 3 4 5 6 7 8 9 0 {bksp}",
                    "Q W E R T Y U I O P",
                    "A S D F G H J K L",
                    "{shift} Z X C V B N M , .",
                    "{space} {enter}"
                  ],
                  shift: [
                    "! @ # $ % ^ & * ( ) {bksp}",
                    "q w e r t y u i o p",
                    "a s d f g h j k l",
                    "{shift} z x c v b n m < >",
                    "{space} {enter}"
                  ]
                }}
                display={{
                  "{bksp}": "⌫",
                  "{enter}": "ENTER",
                  "{shift}": "SHIFT",
                  "{space}": "SPACE",
                }}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default VirtualKeyboard;
